import { ThemeProvider } from '@material-ui/core/styles';
import React from 'react';
import ReactDOM from 'react-dom';
import smoothscroll from 'smoothscroll-polyfill';
import { ErrorBoundary, startBugSnag } from '../bugsnag';
import {
  checkOldBrowser,
  generateBrowserWarningElement,
} from '../helpers/browserDetect';
import mergeConfigs from '../helpers/config';
import makeTheme from '../theme';
import App, { AppError } from './App';
import { initSession } from './service';

smoothscroll.polyfill();

// Used for verifying the parent API window origin.
let currentOrigin;

// Default config for self-assessment
const DEFAULT_CONFIG = {
  baseURI: new URL('/api/osa', window.location).href,
  locale: 'en_US',
  steps: ['questionnaire', 'clinicForm', 'contactDetails', 'completed'],
};

const RTL_LANGUAGES = [
  'he',
  'ar',
  'dv',
  'fa',
  'ha',
  'ks',
  'ku',
  'ps',
  'ur',
  'yi',
];

// Used to send messages to the parent API. Remember to NEVER send sensitive data back as callbacks.
const postMessage = (type, options) => {
  window.parent.postMessage(JSON.stringify({ type, options }), currentOrigin);
};

const getRootElement = () => document.getElementById('root');

const onClose = (redirectUrl) => {
  postMessage('close', { redirectUrl });
  currentOrigin = undefined;

  ReactDOM.unmountComponentAtNode(getRootElement());
};

// Renders the self-assessment with the specified config.
const render = ({ config, loading, errorCode, sessionInfo, sessionToken }) => {
  ReactDOM.render(
    <ThemeProvider theme={makeTheme(config)}>
      <ErrorBoundary
        FallbackComponent={() => (
          <AppError
            config={config}
            onClose={() => onClose()}
            root={getRootElement()}
          />
        )}
      >
        <App
          config={config}
          errorCode={errorCode}
          loading={loading}
          onAction={(action, metrics) => {
            postMessage('action', {
              action,
              values: metrics,
            });
          }}
          onClose={(redirectUrl) => onClose(redirectUrl)}
          sessionInfo={sessionInfo}
          sessionToken={sessionToken}
        />
      </ErrorBoundary>
    </ThemeProvider>,
    getRootElement(),
  );
};

function buildConfig({
  baseURI = DEFAULT_CONFIG.baseURI,
  locale = DEFAULT_CONFIG.locale,
  ...restConfig
} = {}) {
  const localeMatch = locale.match(/^([a-z]{2,})[_-]([A-Z]{2,})$/);
  if (!localeMatch) {
    return [{}, 'Invalid locale format'];
  }
  const language = localeMatch[1].toLowerCase();
  const country = localeMatch[2].toLowerCase();
  const textDirection = RTL_LANGUAGES.includes(language) ? 'rtl' : undefined;

  const uriMatch = baseURI.match(/^(https?:\/\/.+?)\/?$/);
  if (!uriMatch) {
    return [{}, 'Invalid base URI format'];
  }

  return [
    {
      baseURI: `${uriMatch[1]}`,
      locale,
      language,
      country,
      textDirection,
      ...restConfig,
    },
  ];
}

// All functions of the public API.
// Remember to add new functions to the functions array in `api.js`.
// All functions need to follow the signature of `f(callbackId, options)` and they need to post a message
// with either `error` or `done` together with the `callbackId`.
const api = {
  /**
   * Opens the self-assessment.
   *
   * @param {Object} config - The configuration to use when opening the self-assessment.
   * @param {string} config.locale - The locale code (`en_US`, `sv_SE`, ...).
   * @param {string} config.language - The ISO 639-1 language code (`en`, `sv`, ...).
   * @param {string} config.country - The country code (`us`, `se`, ...).
   * @param {string} config.studyId - The Trialbee Study ID.
   * @param {string} config.referrer - The URL that the candidate was referred from.
   * @param {string} config.referrerToken - The token as an open string eg: `Trialbee`.
   * @param {string} config.welcomeImage - The banner image to show on the introduction page.
   * @param {string} [config.baseURI] - The base URI for API calls.
   * @param {string} [config.googleApiKey] - A Google API key used for accessing the Google Maps API.
   * @param {string} [config.colorMain] - The theme color to use. Defaults to Trialbee blue.
   * @param {string} [config.colorLight] - The light theme color to use. Defaults to lightenand desaturated `colorMain`.
   * @param {string} [config.fontFamily] - The font to use.
   * @param {string} [config.fontInclude] - An url to CSS with `font-face` to be included.
   */
  open(callbackId, partialConfig) {
    if (checkOldBrowser(window.navigator.userAgent)) {
      const el = generateBrowserWarningElement(() => {
        postMessage('close');
      });
      getRootElement().appendChild(el);
      postMessage('open');
      return undefined;
    }

    const sendError = (message) => {
      postMessage('error', {
        fatal: true,
        message,
        callbackId,
      });
      currentOrigin = null;
    };

    const [config, error] = buildConfig(partialConfig);
    if (error) return sendError(error);

    startBugSnag(config);
    render({ config, loading: true });
    postMessage('open');

    return initSession(config)
      .then(([sessionInfo, sessionToken]) => {
        const merged = mergeConfigs(
          DEFAULT_CONFIG,
          sessionInfo.config,
          { textDirection: sessionInfo.textDirection },
          config,
        );
        // Apply custom fonts style
        if (merged.fontInclude) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = merged.fontInclude;
          document.head.appendChild(link);
        }

        document.body.setAttribute('dir', merged.textDirection);
        document.documentElement.setAttribute('lang', merged.language);

        return render({
          sessionInfo,
          sessionToken,
          config: merged,
        });
      })
      .catch((errorCode) => {
        const errorConfig = mergeConfigs(DEFAULT_CONFIG, config);
        render({ config: errorConfig, errorCode });
      })
      .finally(() => postMessage('done', { callbackId }));
  },

  getHeight(callbackId) {
    try {
      const content = document.getElementsByName('tb-sa-content')[0];
      content.style.display = 'block';
      content.style.overflow = 'scroll';
      postMessage('setHeight', {
        scrollHeight: content.scrollHeight,
        callbackId,
      });
      content.style.overflow = null;
      content.style.display = null;
    } catch (err) {
      postMessage('error', {
        message: `${err}`,
        callbackId,
      });
    }
  },
};

// Listens for messages from the parent API.
window.addEventListener('message', (e) => {
  if (typeof e.data !== 'string') return;
  if (currentOrigin && e.origin !== currentOrigin) return;

  try {
    const { f, callbackId, options } = JSON.parse(e.data);

    if (f === 'open') {
      // The dialog must already be opened so we ignore this event.
      if (currentOrigin) return;
      // We set the current origin here to verify the top level API later for callbacks and such.
      currentOrigin = e.origin;
    }
    api[f](callbackId, options);
  } catch (err) {
    // We don't really care about this error since it probably means some sort of attack is going on.
  }
});
